/**
 * LoginMascotShowcase — all mascots bouncing freely across the viewport
 * with billiard-ball physics and mouse-flee behaviour.
 *
 * Single rAF loop drives all mascots; mouse is tracked once and shared.
 */

import { useMemo, useEffect, useRef } from 'react';
import { getAllMascots } from '@/styles/playful';
import { typography } from '@/styles/design-tokens';
import { MASCOT_COMPONENTS } from './mascots';

// --- Physics tuning ---
const BASE_SPEED = 0.022; // px per ms
const MASCOT_SIZE = 32; // px (approx rendered size)
const FLEE_RADIUS = 140; // px
const FLEE_BOOST = 2.6;
const BOUNCE_JITTER = 0.18; // ± radians

interface Vec2 {
  x: number;
  y: number;
}

function randomAngle(): number {
  const a = Math.random() * Math.PI * 2;
  if (Math.abs(Math.cos(a)) < 0.15 || Math.abs(Math.sin(a)) < 0.15)
    return randomAngle();
  return a;
}
function jitter(): number {
  return (Math.random() - 0.5) * 2 * BOUNCE_JITTER;
}
function norm(v: Vec2): Vec2 {
  const m = Math.sqrt(v.x * v.x + v.y * v.y);
  return m > 0 ? { x: v.x / m, y: v.y / m } : v;
}
function rot(v: Vec2, r: number): Vec2 {
  return {
    x: v.x * Math.cos(r) - v.y * Math.sin(r),
    y: v.x * Math.sin(r) + v.y * Math.cos(r),
  };
}
function speedMult(now: number): number {
  const s =
    (Math.sin(now / 8000) +
      Math.sin(now / 3400 + 1.7) * 0.7 +
      Math.sin(now / 13000 + 4.2) * 0.7) /
    2.4;
  return 0.3 + ((s + 1) / 2) * 1.4;
}

interface MascotDef {
  emoji: string;
  themeId: string;
  size: number;
  opacity: number;
}

export function LoginMascotShowcase() {
  const mascots = useMemo(() => getAllMascots(), []);

  const defs = useMemo((): MascotDef[] => {
    if (mascots.length < 7) return [];
    const m = mascots;
    // 3 copies of each of the 7 themes = 21 mascots, varied sizes
    return [
      { emoji: m[0].emoji, themeId: m[0].themeId, size: 30, opacity: 0.65 },
      { emoji: m[1].emoji, themeId: m[1].themeId, size: 26, opacity: 0.6 },
      { emoji: m[2].emoji, themeId: m[2].themeId, size: 34, opacity: 0.55 },
      { emoji: m[3].emoji, themeId: m[3].themeId, size: 22, opacity: 0.6 },
      { emoji: m[4].emoji, themeId: m[4].themeId, size: 28, opacity: 0.65 },
      { emoji: m[5].emoji, themeId: m[5].themeId, size: 32, opacity: 0.55 },
      { emoji: m[6].emoji, themeId: m[6].themeId, size: 24, opacity: 0.6 },
      { emoji: m[0].emoji, themeId: m[0].themeId, size: 20, opacity: 0.45 },
      { emoji: m[1].emoji, themeId: m[1].themeId, size: 36, opacity: 0.5 },
      { emoji: m[2].emoji, themeId: m[2].themeId, size: 18, opacity: 0.4 },
      { emoji: m[3].emoji, themeId: m[3].themeId, size: 28, opacity: 0.5 },
      { emoji: m[4].emoji, themeId: m[4].themeId, size: 22, opacity: 0.45 },
      { emoji: m[5].emoji, themeId: m[5].themeId, size: 16, opacity: 0.4 },
      { emoji: m[6].emoji, themeId: m[6].themeId, size: 30, opacity: 0.55 },
      { emoji: m[0].emoji, themeId: m[0].themeId, size: 24, opacity: 0.5 },
      { emoji: m[2].emoji, themeId: m[2].themeId, size: 26, opacity: 0.45 },
      { emoji: m[4].emoji, themeId: m[4].themeId, size: 32, opacity: 0.6 },
      { emoji: m[1].emoji, themeId: m[1].themeId, size: 18, opacity: 0.4 },
      { emoji: m[3].emoji, themeId: m[3].themeId, size: 20, opacity: 0.45 },
      { emoji: m[5].emoji, themeId: m[5].themeId, size: 28, opacity: 0.5 },
      { emoji: m[6].emoji, themeId: m[6].themeId, size: 22, opacity: 0.55 },
    ];
  }, [mascots]);

  const elRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const mouseRef = useRef<Vec2>({ x: -9999, y: -9999 });

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  useEffect(() => {
    if (defs.length === 0) return;

    const count = defs.length;
    const W = window.innerWidth;
    const H = window.innerHeight;

    // Per-mascot physics state
    const pos: Vec2[] = Array.from({ length: count }, () => ({
      x: Math.random() * (W - MASCOT_SIZE),
      y: Math.random() * (H - MASCOT_SIZE),
    }));
    const vel: Vec2[] = Array.from({ length: count }, () => {
      const a = randomAngle();
      return { x: Math.cos(a), y: Math.sin(a) };
    });
    const lastBounce = new Float64Array(count);
    const lastMascotBounce = new Float64Array(count); // per-mascot cooldown for mascot–mascot collisions
    let lastFrame = 0;
    let raf = 0;

    // Precompute hitbox radii
    const radii = defs.map(d => ((d.size / 16) * MASCOT_SIZE) / 2);

    const tick = (now: number) => {
      raf = requestAnimationFrame(tick);
      if (!lastFrame) {
        lastFrame = now;
        return;
      }
      const dt = Math.min(now - lastFrame, 50);
      lastFrame = now;

      const speed = BASE_SPEED * speedMult(now);
      const mouse = mouseRef.current;
      const vW = window.innerWidth;
      const vH = window.innerHeight;

      // Mascot–mascot collisions (elastic, equal mass)
      for (let i = 0; i < count; i++) {
        if (now - lastMascotBounce[i] < 300) continue;
        for (let j = i + 1; j < count; j++) {
          if (now - lastMascotBounce[j] < 300) continue;
          const ri = radii[i],
            rj = radii[j];
          const dx = pos[i].x + ri - (pos[j].x + rj);
          const dy = pos[i].y + ri - (pos[j].y + rj);
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < ri + rj && dist > 0.01) {
            // Collision normal (i relative to j)
            const nx = dx / dist,
              ny = dy / dist;
            // Only resolve if approaching
            const dvx = vel[i].x - vel[j].x;
            const dvy = vel[i].y - vel[j].y;
            const approach = dvx * nx + dvy * ny;
            if (approach < 0) {
              // Elastic impulse (equal mass)
              vel[i] = norm(
                rot(
                  { x: vel[i].x - approach * nx, y: vel[i].y - approach * ny },
                  jitter()
                )
              );
              vel[j] = norm(
                rot(
                  { x: vel[j].x + approach * nx, y: vel[j].y + approach * ny },
                  jitter()
                )
              );
              // Separate overlapping mascots
              const overlap = (ri + rj - dist) / 2;
              pos[i].x += nx * overlap;
              pos[i].y += ny * overlap;
              pos[j].x -= nx * overlap;
              pos[j].y -= ny * overlap;
              lastMascotBounce[i] = now;
              lastMascotBounce[j] = now;
            }
          }
        }
      }

      for (let i = 0; i < count; i++) {
        const p = pos[i];
        const v = vel[i];
        const size = radii[i] * 2;

        // Mouse flee
        const cx = p.x + size / 2;
        const cy = p.y + size / 2;
        const mdx = cx - mouse.x;
        const mdy = cy - mouse.y;
        const dist = Math.sqrt(mdx * mdx + mdy * mdy);
        let moveSpeed = speed;

        if (dist < FLEE_RADIUS && dist > 1) {
          const prox = 1 - dist / FLEE_RADIUS;
          const flee = norm({ x: mdx, y: mdy });
          const steer = Math.min(0.3, prox * 0.35);
          vel[i] = norm({
            x: v.x * (1 - steer) + flee.x * steer,
            y: v.y * (1 - steer) + flee.y * steer,
          });
          moveSpeed = speed * (1 + prox * FLEE_BOOST);
        }

        p.x += vel[i].x * moveSpeed * dt;
        p.y += vel[i].y * moveSpeed * dt;

        // Viewport bounce
        let bounced = false;
        if (p.x <= 0) {
          p.x = 0;
          vel[i] = norm(rot({ x: Math.abs(vel[i].x), y: vel[i].y }, jitter()));
          bounced = true;
        } else if (p.x >= vW - size) {
          p.x = vW - size;
          vel[i] = norm(rot({ x: -Math.abs(vel[i].x), y: vel[i].y }, jitter()));
          bounced = true;
        }
        if (p.y <= 0) {
          p.y = 0;
          vel[i] = norm(rot({ x: vel[i].x, y: Math.abs(vel[i].y) }, jitter()));
          bounced = true;
        } else if (p.y >= vH - size) {
          p.y = vH - size;
          vel[i] = norm(rot({ x: vel[i].x, y: -Math.abs(vel[i].y) }, jitter()));
          bounced = true;
        }
        if (bounced) lastBounce[i] = now;

        const el = elRefs.current[i];
        if (el) {
          el.style.left = `${p.x.toFixed(1)}px`;
          el.style.top = `${p.y.toFixed(1)}px`;
        }
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [defs]);

  if (defs.length === 0) return null;

  return (
    <>
      {defs.map((d, i) => (
        <span
          key={i}
          ref={el => {
            elRefs.current[i] = el;
          }}
          aria-hidden
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            fontSize: d.size,
            lineHeight: typography.lineHeights.tight,
            opacity: d.opacity,
            pointerEvents: 'none',
            userSelect: 'none',
            zIndex: 0,
            willChange: 'left, top',
          }}
        >
          {(() => {
            const SvgMascot = MASCOT_COMPONENTS[d.themeId];
            return SvgMascot ? <SvgMascot /> : d.emoji;
          })()}
        </span>
      ))}
    </>
  );
}
